import React, { Component } from 'react';
import { debounce } from 'lodash';
import { inject, observer } from 'mobx-react';
import { AutoSizer } from 'react-virtualized';
import { Box, EmptyState, Flex, Text } from 'core/components';
import storeLoader from 'app/stores/storeLoader';
import { updateTimelineChartSelection } from 'app/views/synthetics/utils/syntheticsUtils';
import AlarmTimeline from 'app/views/synthetics/components/alarmTimeline/AlarmTimeline';
import TestResultsTable from 'app/views/synthetics/components/testResults/TestResultsTable';
import LoadingPanel from 'app/views/synthetics/components/testResults/panels/LoadingPanel';
import ToolResultCard from 'app/views/core/kaizen/components/ToolResultCard';
import AggregationTag from 'app/views/synthetics/components/AggregationTag';
import MetricsView from 'app/views/synthetics/components/agentResults/MetricsView';
import ResultsView from 'app/views/synthetics/components/agentResults/ResultsView';
import LoadingSkeleton from './LoadingSkeleton';

@storeLoader('$syn.agents', '$syn.tests')
@inject('$dataviews')
@observer
export default class SyntheticsTestResultsToolResult extends Component {
  state = {};

  static getDerivedStateFromProps(props, state) {
    const { $syn, loading, tool } = props;
    const { timestamps, results, aggregation, type, target } = tool?.raw_result || {};
    const { test_id, agent_id, start, end } = tool?.query_object || {};

    if (!loading && !state.testModel) {
      // use a copy of the test model to ensure we don't share test results between detail panes
      const testModel = $syn.getTest(test_id)?.duplicate({ removeId: false, save: false, deserialize: false });

      if (testModel) {
        if (type === 'subtest') {
          testModel.agentResults.loadResults({ agent_id, target, start, end, timestamps, results });
        } else {
          testModel.results.loadResults({ start, end, timestamps, results, aggregation });
        }
        return { testModel, timestamps };
      }
    }

    return null;
  }

  componentWillUnmount() {
    // clean up debounced calls to timeline/series chart cursor syncing
    this.selectTimelinePoints?.cancel();
  }

  selectTimelinePoints = debounce((resultTimeMs) => {
    const { $dataviews } = this.props;

    updateTimelineChartSelection($dataviews, resultTimeMs);
  }, 150);

  onChangeResultTimeMs = (resultTimeMs) => {
    this.testResults.onChangeResultTimeMs(resultTimeMs);
  };

  get testResults() {
    const { tool } = this.props;
    const { testModel } = this.state;
    const { type } = tool?.raw_result || {};

    if (type === 'subtest') {
      return testModel.agentResults;
    }

    return testModel.results;
  }

  get title() {
    const { tool } = this.props;
    return (
      <Flex alignItems="flex-start" gap={1} mr={1}>
        <Text>{tool?.title || 'Test Results'}</Text>
        <AggregationTag testResults={this.testResults} minWidth="fit-content" />
      </Flex>
    );
  }

  get parentTestResults() {
    const { testResults } = this;
    const { tool } = this.props;
    const { type } = tool?.raw_result || {};
    const { testModel } = this.state;

    if (type === 'parent') {
      return (
        <LoadingPanel
          test={testModel}
          testResults={testResults}
          loading={testResults.loading && !testResults.hasResults}
          empty={testResults.healthTimeline.length === 0 || !testResults.hasResults}
        >
          <TestResultsTable
            test={testModel}
            results={testResults.results}
            agentTaskConfig={testResults.agentTaskConfig}
            resultTimeMs={testResults.resultTimeMs}
            allowAgentDetails={testResults.displayOptions.allowAgentDetails}
          >
            {(resultsTable) => (
              <>
                {resultsTable.footer}
                {resultsTable.allResults}
              </>
            )}
          </TestResultsTable>
        </LoadingPanel>
      );
    }

    return null;
  }

  get subtestResults() {
    const { testResults } = this;
    const { tool } = this.props;
    const { testModel } = this.state;
    const { agent_id } = tool?.query_object || {};
    const { type, target } = tool?.raw_result || {};
    const targetResults = testResults?.agents?.get(`${agent_id}`)?.targets?.get(target);

    if (type === 'subtest') {
      return (
        <LoadingPanel empty={!targetResults}>
          {testModel?.isTransaction ? (
            <ResultsView
              test={testModel}
              httpHealthTs={targetResults?.httpHealthTs}
              httpHealthAggregates={targetResults?.httpHealthAggregates}
              screenshotsTsResult={targetResults?.screenshotsTsResult}
              resultTimeMs={testResults?.resultTimeMs}
              timelineBounds={testResults?.timelineBounds}
              onHover={(resultTimeMs) => this.selectTimelinePoints(resultTimeMs)}
            />
          ) : (
            <Box overflow="auto" p={1}>
              <MetricsView
                flex={1}
                test={testModel}
                agent_id={agent_id}
                target={target}
                pingHealthTs={targetResults?.pingHealthTs}
                pingHealthAggregates={targetResults?.pingHealthAggregates}
                dnsHealthTs={targetResults?.dnsHealthTs}
                dnsHealthTsResult={targetResults?.dnsHealthTsResult}
                dnsHealthAggregates={targetResults?.dnsHealthAggregates}
                httpHealthTs={targetResults?.httpHealthTs}
                httpHealthAggregates={targetResults?.httpHealthAggregates}
                throughputHealthTs={targetResults?.throughputHealthTs}
                throughputHealthAggregates={targetResults?.throughputHealthAggregates}
                timelineBounds={testResults?.timelineBounds}
                isAggregated={testResults?.isAggregated}
                onHover={(resultTimeMs) => this.selectTimelinePoints(resultTimeMs)}
              />
            </Box>
          )}
        </LoadingPanel>
      );
    }

    return null;
  }

  render() {
    const { tool = {}, onCloseToolResult, isInlineMode, loading } = this.props;
    const { type } = tool?.raw_result || {};
    const { testModel } = this.state;

    if (loading) {
      return <LoadingSkeleton />;
    }

    if (!testModel) {
      return <EmptyState m={2} icon="disable" title="Test Not Found" />;
    }

    const { testResults } = this;

    return (
      <ToolResultCard
        tool={tool}
        onCloseToolResult={onCloseToolResult}
        isInlineMode={isInlineMode}
        title={this.title}
        minHeight={750}
        px={2}
      >
        <LoadingPanel loading={testResults.loadingAlarmResults}>
          <AutoSizer disableHeight>
            {({ width }) => (
              <AlarmTimeline
                {...testResults.timelineBounds}
                width={width}
                data={testResults.healthTimeline}
                testResults={testResults}
                onHover={type === 'subtest' ? this.selectTimelinePoints : undefined} // subtest results cursor tracking
                onSelect={type === 'parent' ? this.onChangeResultTimeMs : undefined} // parent test results navigation
              />
            )}
          </AutoSizer>
        </LoadingPanel>
        {this.parentTestResults}
        {this.subtestResults}
      </ToolResultCard>
    );
  }
}
